import type { TadaDocumentNode } from 'gql.tada';
import { useQuery, useSubscription } from 'urql';
import { graphql } from '@/gql/graphql';
import useOffsetPagination from './useOffsetPagination';

const NoopQuery = graphql(/* GraphQL */ `
  query NoopQuery {
    __typename
  }
`);

const NoopSubscription = graphql(/* GraphQL */ `
  subscription NoopSubscription {
    __typename
  }
`);

export default function useSearchablePaginatedQuery<
  TItem,
  TGetData,
  TGetVariables extends Record<string, unknown>,
  TSearchData,
  TSearchVariables extends Record<string, unknown>,
  TWatchData,
  TWatchVariables extends Record<string, unknown>,
>({
  searchTerm,
  searchOnMinimum,
  pageSize,
  getQuery,
  getVariables,
  selectGetItems,
  selectGetTotal,
  searchQuery,
  searchVariables,
  selectSearchItems,
  watchQuery,
  watchVariables,
  watchEnabled,
}: {
  searchTerm: string;
  searchOnMinimum: number;
  pageSize: number;
  getQuery: TadaDocumentNode<TGetData, TGetVariables>;
  getVariables(args: { limit: number; offset: number }): TGetVariables;
  selectGetItems(data: TGetData | undefined): readonly TItem[];
  selectGetTotal?(data: TGetData | undefined): number;
  searchQuery?: TadaDocumentNode<TSearchData, TSearchVariables>;
  searchVariables?(args: { searchTerm: string }): TSearchVariables;
  selectSearchItems?(data: TSearchData | undefined): readonly TItem[];
  watchQuery?: TadaDocumentNode<TWatchData, TWatchVariables>;
  watchVariables?(args: { limit: number; offset: number }): TWatchVariables;
  watchEnabled?: boolean;
}) {
  const pagination = useOffsetPagination({ pageSize });
  const isSearching = searchTerm.length >= searchOnMinimum;

  const resolvedGetVariables = getVariables({
    limit: pagination.limit,
    offset: pagination.offset,
  });

  const resolvedSearchVariables = (() => {
    if (!searchQuery || !searchVariables) {
      return null;
    }

    return searchVariables({ searchTerm });
  })();

  const [{ data: getData, fetching: fetchingGet }] = useQuery({
    query: getQuery,
    variables: resolvedGetVariables,
    requestPolicy: 'cache-and-network',
    pause: isSearching,
  });

  const [{ data: searchData, fetching: fetchingSearch }] = useQuery({
    query: (searchQuery ?? NoopQuery) as TadaDocumentNode<
      TSearchData,
      TSearchVariables
    >,
    variables: resolvedSearchVariables as TSearchVariables,
    requestPolicy: 'cache-and-network',
    pause: !isSearching || !searchQuery || !resolvedSearchVariables,
  });

  useSubscription({
    query: (watchQuery ?? NoopSubscription) as TadaDocumentNode<
      TWatchData,
      TWatchVariables
    >,
    variables: watchVariables
      ? watchVariables({ limit: pagination.limit, offset: pagination.offset })
      : (undefined as unknown as TWatchVariables),
    pause: !watchEnabled || !watchQuery,
  });

  const items: readonly TItem[] =
    isSearching && selectSearchItems
      ? selectSearchItems(searchData)
      : selectGetItems(getData);

  const total = selectGetTotal ? selectGetTotal(getData) : undefined;
  const fetching = isSearching ? fetchingSearch : fetchingGet;

  return {
    items,
    total,
    fetching,
    isSearching,
    pagination,
  };
}
